import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@capsico/ui';
import type { CountryCode, OutletCard } from '@capsico/contracts';
import { useI18n, type Translate } from '../i18n';
import { cardLabels } from '../i18n/labels';
import { useDeliveryPoint } from '../hooks/useDeliveryPoint';
import { useHomeFeed } from '../hooks/useHomeFeed';
import { useSession } from '../hooks/useSession';
import { useAreaVote } from '../hooks/useAreaVote';
import { useMyReviews, type RateTarget } from '../hooks/useReviews';
import { RateStrip } from '../components/Reviews';
import { BellButton } from '../components/Inbox';
import { useUnreadCount } from '../hooks/useInbox';
import { RateFlow } from './RateFlow';
import { LocationBar } from '../components/LocationBar';
import { LocationChooser } from '../components/LocationChooser';
import { RestaurantCard } from '../components/RestaurantCard';
import { AreaVoteCard } from '../components/AreaVoteCard';
import { TurnstileBox } from '../components/TurnstileBox';
import { Button, EmptyState, ListSkeleton, Notice, SectionTitle } from '../components/ui';

/**
 * Home (C4): "Deliver to" bar → search bar → Capsico promise strip → cuisines → "What's on your
 * mind?" → top restaurants → other rows. Not served here → area vote. No point yet → ask.
 */
export function HomePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const session = useSession();
  const point = useDeliveryPoint();
  const home = useHomeFeed(point);
  const [choosing, setChoosing] = useState(false);
  const mine = useMyReviews(session.signedIn);
  const [rateTarget, setRateTarget] = useState<RateTarget | null>(null);
  const firstPending = mine.pending[0] ?? null;
  const country = home.area?.country ?? null;
  const unread = useUnreadCount(session.signedIn);

  return (
    <div className="min-h-dvh bg-page">
      <header className="sticky top-0 z-30 bg-surface px-4 pt-[max(env(safe-area-inset-top),12px)] pb-3">
        <div className="flex items-center gap-3">
          <LocationBar
            caption={t('location.deliverTo')}
            label={point?.label || t('location.choose')}
            line={point?.line ?? null}
            onOpen={() => setChoosing(true)}
          />
          {session.signedIn && (
            <BellButton
              label={unread ? t('inbox.bellUnread', { count: unread }) : t('inbox.bell')}
              count={unread}
              onClick={() => navigate('/account/inbox')}
            />
          )}
          <button
            type="button"
            aria-label={t('nav.account')}
            onClick={() => navigate('/account')}
            className="inline-flex size-9 items-center justify-center rounded-full bg-tile text-ink"
          >
            <Icon name="user" size={18} />
          </button>
        </div>
        <button
          type="button"
          onClick={() => navigate('/search')}
          className="mt-3 flex h-11 w-full items-center gap-2 rounded-xl bg-surface px-3 text-left text-sm text-ink-3 shadow-[inset_0_0_0_1px_var(--color-field)]"
        >
          <Icon name="search" size={18} />
          <span className="truncate">{t('home.searchPlaceholder')}</span>
        </button>
      </header>

      {!point ? (
        <EmptyState
          icon="pin"
          title={t('location.choose')}
          action={<Button onClick={() => setChoosing(true)}>{t('location.useCurrent')}</Button>}
        >
          {t('location.setFirst')}
        </EmptyState>
      ) : home.loading ? (
        <div className="space-y-5 px-4 pt-5">
          <ListSkeleton rows={3} tall />
        </div>
      ) : home.failed ? (
        <div className="px-4 pt-5">
          <Notice
            tone="negative"
            supportRef
            title={t('common.unreachable')}
            action={
              <Button size="sm" tone="outline" onClick={home.retry}>
                {t('common.retry')}
              </Button>
            }
          >
            {t('common.unreachableBody')}
          </Notice>
        </div>
      ) : home.area && !home.area.serviceable ? (
        <AreaVote
          point={point}
          signedIn={session.signedIn}
          onOtherPlace={() => setChoosing(true)}
        />
      ) : (
        <div className="flex flex-col gap-5 pt-4 pb-6">
          {firstPending && (
            <div className="px-4">
              <RateStrip
                title={t('rate.stripTitle')}
                body={
                  mine.pending.length > 1
                    ? t('rate.stripMany', {
                        name: firstPending.outletName,
                        count: mine.pending.length - 1,
                      })
                    : t('rate.stripOne', { name: firstPending.outletName })
                }
                actionLabel={t('rate.stripAction')}
                onRate={() => setRateTarget({ kind: 'new', pending: firstPending })}
                onOpenAll={() => navigate('/account/reviews')}
              />
            </div>
          )}
          <PromiseStrip t={t} />
          {home.feed && home.feed.cuisines.length > 0 && (
            <section className="px-4">
              <SectionTitle>{t('home.cuisines')}</SectionTitle>
              <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4">
                {home.feed.cuisines.map((cuisine) => (
                  <button
                    key={cuisine.name}
                    type="button"
                    onClick={() => navigate(`/search?cuisine=${encodeURIComponent(cuisine.name)}`)}
                    className="h-9 shrink-0 rounded-full bg-surface px-4 text-xs font-medium text-ink shadow-[inset_0_0_0_1px_var(--color-control)]"
                  >
                    {cuisine.name}
                  </button>
                ))}
              </div>
            </section>
          )}
          {home.feed && home.feed.mind.length > 0 && (
            <section className="px-4">
              <SectionTitle>{t('home.mind')}</SectionTitle>
              <div className="no-scrollbar -mx-4 grid auto-cols-[76px] grid-flow-col grid-rows-2 gap-x-3 gap-y-3 overflow-x-auto px-4">
                {home.feed.mind.map((tile) => (
                  <button
                    key={tile.name}
                    type="button"
                    onClick={() => navigate(`/search?q=${encodeURIComponent(tile.query)}`)}
                    className="flex flex-col items-center gap-1"
                  >
                    {tile.imageUrl ? (
                      <img
                        src={tile.imageUrl}
                        alt=""
                        loading="lazy"
                        className="size-16 rounded-full object-cover"
                      />
                    ) : (
                      <span className="flex size-16 items-center justify-center rounded-full bg-brand-soft text-xl font-bold text-brand-ink">
                        {tile.name.slice(0, 1)}
                      </span>
                    )}
                    <span className="w-full truncate text-center text-caption text-ink-2">
                      {tile.name}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}
          {home.otherRows.map((row) => (
            <section key={row.key} className="px-4">
              <SectionTitle>{row.title}</SectionTitle>
              {row.subtitle && <p className="-mt-2 mb-2.5 text-xs text-ink-3">{row.subtitle}</p>}
              <div className="no-scrollbar -mx-4 flex gap-3 overflow-x-auto px-4">
                {row.items.map((outlet) => (
                  <RestaurantCard
                    key={outlet.outletId}
                    outlet={outlet}
                    country={country}
                    labels={cardLabels(t)}
                    compact
                    onOpen={() => navigate(`/r/${outlet.outletId}`)}
                  />
                ))}
              </div>
            </section>
          ))}
          <TopList
            t={t}
            items={home.top?.items ?? []}
            country={country}
            onOpen={(id) => navigate(`/r/${id}`)}
          />
        </div>
      )}

      {choosing && (
        <LocationChooser signedIn={session.signedIn} onClose={() => setChoosing(false)} />
      )}
      {rateTarget && rateTarget.kind === 'new' && (
        <RateFlow
          key={rateTarget.pending.orderId}
          target={rateTarget}
          onClose={() => setRateTarget(null)}
        />
      )}
    </div>
  );
}

function TopList({
  t,
  items,
  country,
  onOpen,
}: {
  t: Translate;
  items: OutletCard[];
  country: CountryCode | null;
  onOpen: (outletId: string) => void;
}) {
  if (items.length === 0) {
    return (
      <EmptyState icon="flame" title={t('home.emptyTitle')}>
        {t('home.emptyBody')}
      </EmptyState>
    );
  }
  return (
    <section className="px-4">
      <SectionTitle
        action={
          <span className="text-xs text-ink-3">{t('home.places', { count: items.length })}</span>
        }
      >
        {t('home.top')}
      </SectionTitle>
      <div className="space-y-4">
        {items.map((outlet) => (
          <RestaurantCard
            key={outlet.outletId}
            outlet={outlet}
            country={country}
            labels={cardLabels(t)}
            onOpen={() => onOpen(outlet.outletId)}
          />
        ))}
      </div>
    </section>
  );
}

/** Capsico's three promises (no offers engine yet) — a swipeable strip of tinted cards. */
function PromiseStrip({ t }: { t: Translate }) {
  const cards = [
    { icon: 'wallet' as const, title: t('home.promiseTitle1'), body: t('home.promiseBody1') },
    { icon: 'tag' as const, title: t('home.promiseTitle2'), body: t('home.promiseBody2') },
    { icon: 'store' as const, title: t('home.promiseTitle3'), body: t('home.promiseBody3') },
  ];
  return (
    <div className="no-scrollbar flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4">
      {cards.map((card) => (
        <div
          key={card.title}
          className="flex w-4/5 shrink-0 snap-start items-start gap-3 rounded-2xl bg-brand-soft p-4"
        >
          <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-brand text-on-brand">
            <IconFor name={card.icon} />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-ink">{card.title}</p>
            <p className="mt-0.5 text-xs text-ink-2">{card.body}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function IconFor({ name }: { name: 'wallet' | 'tag' | 'store' }) {
  return <Icon name={name} size={18} />;
}

function AreaVote({
  point,
  signedIn,
  onOtherPlace,
}: {
  point: { lat: number; lng: number };
  signedIn: boolean;
  onOtherPlace: () => void;
}) {
  const { t } = useI18n();
  const vote = useAreaVote(point, signedIn);
  return (
    <AreaVoteCard
      labels={{
        title: t('vote.title'),
        body: t('vote.body'),
        cta: t('vote.cta'),
        voted: t('vote.voted'),
        already: t('vote.already'),
        nearby: (count) => t('vote.nearby', { count }),
        notify: t('vote.notify'),
        otherPlace: t('vote.otherPlace'),
      }}
      votesNearby={vote.votesNearby}
      showPhone={vote.showPhone}
      phone={vote.phone}
      onPhone={vote.setPhone}
      onVote={vote.submit}
      voting={vote.voting}
      result={vote.result}
      error={vote.error}
      check={
        vote.turnstile.enabled ? (
          <TurnstileBox
            boxRef={vote.turnstile.ref}
            label={t('turnstile.label')}
            error={vote.turnstile.error}
          />
        ) : undefined
      }
      onOtherPlace={onOtherPlace}
    />
  );
}
